/**
 * Resolves a BCP-47 language tag to the `messages_xx.js` catalog that the server would load for it.
 *
 * This MIRRORS the mapping in `MainService/src/main/jte/layouts/basic_layout.jte` (the
 * `localeMessagesScript` block), which is what actually picks the catalog `<script>` at page load.
 * The two copies must agree; `MessageCatalog.spec.ts` is table-driven over every `SeavienLanguageTag`
 * so adding a locale on one side without the other fails the build.
 *
 * Why a second copy exists at all: `DocumentSwap` needs to decide, in the browser, whether a soft
 * navigation would require a different chrome-message catalog — and the JTE mapping is server-side
 * template code with no runtime representation the frontend can read. Duplication is the accepted cost
 * of comparing catalog identity instead of the raw tag; see
 * `designs/01-now/2026-07-24-cross-locale-navigation-hard-reload-design.md` (option C).
 */

const DEFAULT_CATALOG_KEY = "messages_en";

/**
 * Prefix → catalog, in the SAME order as the JTE chain. Order is load-bearing:
 * `"fil".startsWith("fi")` is true, so Filipino MUST be tested before Finnish. Keep this list ordered,
 * not sorted.
 */
const CATALOG_BY_PREFIX: ReadonlyArray<readonly [string, string]> = [
    ["pt", "messages_pt_br"],
    ["fr", "messages_fr"],
    ["de", "messages_de"],
    ["it", "messages_it"],
    ["ro", "messages_ro"],
    ["nl", "messages_nl"],
    ["pl", "messages_pl"],
    ["sv", "messages_sv"],
    ["da", "messages_da"],
    ["nb", "messages_nb"],
    // Filipino before Finnish — see the ordering note above. "tl" is the legacy Tagalog alias that
    // `SeavienLanguageTag.fromString` also accepts.
    ["fil", "messages_fil"],
    ["tl", "messages_fil"],
    ["fi", "messages_fi"],
    ["tr", "messages_tr"],
    ["cs", "messages_cs"],
    ["hu", "messages_hu"],
    ["id", "messages_id"],
    ["vi", "messages_vi"],
    ["hi", "messages_hi"],
    ["ko", "messages_ko"],
    ["ms", "messages_ms"],
    ["th", "messages_th"],
    ["bn", "messages_bn"],
    ["el", "messages_el"],
    ["es", "messages_es"],
];

/**
 * The catalog key for a language tag. Unknown, empty and unparseable tags resolve to the English
 * catalog, matching the JTE chain's trailing `: "messages_en"`.
 */
export function resolveMessageCatalogKey(languageTag: string | null | undefined): string {
    const normalized = languageTag?.trim().toLowerCase() ?? "";

    if (!normalized) {
        return DEFAULT_CATALOG_KEY;
    }

    const match = CATALOG_BY_PREFIX.find(([prefix]) => normalized.startsWith(prefix));

    return match ? match[1] : DEFAULT_CATALOG_KEY;
}
